import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { CaseLabelFields, CaseLabelFormatting, LogoConfig } from "@/hooks/useCaseLabels";

export interface PrintLabelInput {
  fields:     CaseLabelFields;
  formatting: CaseLabelFormatting;
  size:       "4x4" | "2x4";
  quantity:   number;
  logo?:      LogoConfig | null;
}

const PRINT_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/print-label`;

async function callPrintLabel(body: object): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  return fetch(
    PRINT_FUNCTION_URL,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify(body),
    }
  );
}

export interface PrinterCheckResult {
  reachable:     boolean;
  ribbonPercent: number | null;
}

/** TCP + SNMP probe — call this before opening the print dialog or on poll. */
export async function checkPrinterStatus(): Promise<PrinterCheckResult> {
  try {
    const res = await callPrintLabel({ checkOnly: true });
    if (!res.ok) return { reachable: false, ribbonPercent: null };
    const data = await res.json();
    return {
      reachable:     data.reachable === true,
      ribbonPercent: typeof data.ribbonPercent === "number" ? data.ribbonPercent : null,
    };
  } catch {
    return { reachable: false, ribbonPercent: null };
  }
}

/** Backwards-compatible boolean wrapper used by PrintDialog. */
export async function checkPrinterReachable(): Promise<boolean> {
  return (await checkPrinterStatus()).reachable;
}

/** Cancels any stuck/queued jobs and soft-resets the printer over the network. */
export function useResetPrinter() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await callPrintLabel({ resetPrinter: true });
      const data = await res.json();
      if (!data.success) throw new Error(data.error ?? "Reset failed");
      return data;
    },
    onSuccess: () => toast({ title: "Printer reset", description: "Cleared any stuck jobs." }),
    onError: (e: Error) =>
      toast({ title: "Reset failed", description: e.message, variant: "destructive" }),
  });
}

/** Mutation that sends a full print job to the edge function. */
export function usePrintLabel() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: PrintLabelInput) => {
      const res = await callPrintLabel(input);
      const data = await res.json();
      if (!data.success) throw new Error(data.error ?? "Print failed");
      return data;
    },
    onSuccess: (_data, { quantity }) => {
      toast({
        title: `Sent to printer`,
        description: `${quantity} label${quantity !== 1 ? "s" : ""} queued.`,
      });
    },
    onError: (e: Error) =>
      toast({
        title: "Print failed",
        description: e.message,
        variant: "destructive",
      }),
  });
}
